import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listWods } from "@/lib/api";
import { slotLabel } from "@/pages/PtProgrammeBuilder";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const WEEKS = 2;

/** The gym's daily workouts by day and session time, two weeks at a time. Each one opens the
 *  programme it comes from. */
export default function WorkoutCalendar() {
  const navigate = useNavigate();
  const [start, setStart] = useState(() => monday(new Date()));
  const [wods, setWods] = useState(null);
  const today = iso(new Date());

  useEffect(() => {
    setWods(null);
    listWods(iso(start), iso(addDays(start, 7 * WEEKS - 1))).then((r) => setWods(r.wods || []), () => setWods([]));
  }, [start]);

  const byDate = {};
  for (const w of wods || []) (byDate[w.date] = byDate[w.date] || []).push(w);

  return (
    <div className="space-y-3" data-testid="workout-calendar">
      <div className="flex items-center gap-2">
        <Button size="icon" variant="outline" onClick={() => setStart(addDays(start, -7))} title="Previous week"><ChevronLeft className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => setStart(monday(new Date()))}>This week</Button>
        <Button size="icon" variant="outline" onClick={() => setStart(addDays(start, 7))} title="Next week"><ChevronRight className="h-4 w-4" /></Button>
        <span className="text-sm text-zinc-400">
          {start.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – {addDays(start, 7 * WEEKS - 1).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
        </span>
      </div>
      {Array.from({ length: WEEKS }, (_, wk) => (
        <div key={wk} className="grid grid-cols-1 gap-2 sm:grid-cols-7">
          {Array.from({ length: 7 }, (_, i) => {
            const d = addDays(start, wk * 7 + i);
            const day = iso(d);
            return (
              <div key={day} className={`min-h-[110px] rounded-lg border p-2 ${day === today ? "border-emerald-500/50" : "border-zinc-800"}`}>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                  {d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}
                </div>
                {wods === null ? <div className="text-xs text-zinc-600">…</div> : (byDate[day] || []).map((w) => (
                  <button key={w.id} type="button" disabled={!w.programme_id}
                    onClick={() => navigate(`/member-app/programmes/${w.programme_id}`)}
                    className="mb-1 block w-full rounded-md bg-zinc-900 px-2 py-1 text-left hover:bg-zinc-800 disabled:cursor-default">
                    <div className="text-[11px] font-semibold text-emerald-300">{slotLabel(w.slot)}</div>
                    <div className="truncate text-xs text-zinc-100" title={w.title}>{w.title}</div>
                    {w.programme_name && <div className="truncate text-[10px] text-zinc-500">{w.programme_name}</div>}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      ))}
      <p className="text-xs text-zinc-500">
        Schedule workouts by making a Daily workout programme: pick its start date and number of weeks, and give each day&apos;s
        workouts a session time (7 am, 8 am…) or &quot;All sessions&quot;.
      </p>
    </div>
  );
}
